import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Award, BookOpen, CheckCircle2, Clock, GraduationCap, Percent, Users } from 'lucide-react';
import { instrutorService } from '../../shared/services';

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function InstructorStudentsPage() {
  const [cursoSelecionado, setCursoSelecionado] = useState('');

  const { data: resumo } = useQuery({
    queryKey: ['instrutor-resumo'],
    queryFn: instrutorService.getResumo,
  });

  const { data: cursos = [], isLoading: carregandoCursos } = useQuery({
    queryKey: ['instrutor-cursos'],
    queryFn: instrutorService.getCursos,
  });

  const cursoAtual = useMemo(
    () => cursos.find((curso) => curso.cursoId === cursoSelecionado) ?? cursos[0],
    [cursos, cursoSelecionado],
  );

  const { data: alunos = [], isLoading: carregandoAlunos } = useQuery({
    queryKey: ['instrutor-alunos', cursoAtual?.cursoId],
    queryFn: () => instrutorService.getAlunos(cursoAtual!.cursoId),
    enabled: !!cursoAtual?.cursoId,
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap size={30} /> Meus Alunos
            </h1>
            <p className="text-gray-600 mt-1">
              Acompanhe progresso, avaliações e certificados dos alunos dos seus cursos.
            </p>
          </div>
          <Link to="/instructor" className="btn-secondary">Voltar ao painel</Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[
            { rotulo: 'Cursos', valor: resumo?.cursos ?? 0, Icon: BookOpen },
            { rotulo: 'Alunos', valor: resumo?.alunos ?? 0, Icon: Users },
            { rotulo: 'Progresso médio', valor: `${resumo?.progressoMedio ?? 0}%`, Icon: Percent },
            { rotulo: 'Certificados', valor: resumo?.certificados ?? 0, Icon: Award },
            { rotulo: 'Avaliações', valor: resumo?.avaliacoes ?? 0, Icon: CheckCircle2 },
          ].map(({ rotulo, valor, Icon }) => (
            <div key={rotulo} className="card">
              <Icon className="text-primary-600 mb-2" size={20} />
              <p className="text-2xl font-bold text-gray-900">{valor}</p>
              <p className="text-xs text-gray-600">{rotulo}</p>
            </div>
          ))}
        </div>

        {carregandoCursos ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : cursos.length === 0 ? (
          <div className="card text-center py-12">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={56} />
            <h2 className="text-xl font-semibold text-gray-900">Nenhum curso vinculado a você</h2>
            <p className="text-gray-600 mt-2">Crie um curso ou peça ao administrador para definir você como instrutor.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <aside className="lg:col-span-1 space-y-3">
              {cursos.map((curso) => {
                const ativo = curso.cursoId === cursoAtual?.cursoId;
                return (
                  <button
                    key={curso.cursoId}
                    type="button"
                    onClick={() => setCursoSelecionado(curso.cursoId)}
                    className={`w-full text-left card transition-all ${ativo ? 'border-primary-500 ring-1 ring-primary-500' : 'hover:border-gray-300'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{curso.titulo}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {curso.totalAlunos} aluno(s) · {curso.totalAulas} aula(s)
                        </p>
                      </div>
                      <span className="text-xs font-medium text-primary-700">{curso.progressoMedio}%</span>
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-600" style={{ width: `${curso.progressoMedio}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {curso.alunosConcluidos} concluído(s) · {curso.certificados} certificado(s)
                    </p>
                  </button>
                );
              })}
            </aside>

            <section className="lg:col-span-2 card overflow-x-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{cursoAtual?.titulo}</h2>
                  <p className="text-sm text-gray-600">
                    {cursoAtual?.totalAlunos} aluno(s) · {cursoAtual?.totalAulas} aula(s) ·{' '}
                    {cursoAtual?.tentativas} tentativa(s) · {cursoAtual?.aprovados} aprovado(s)
                  </p>
                </div>
              </div>

              {carregandoAlunos ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
                </div>
              ) : alunos.length === 0 ? (
                <p className="text-sm text-gray-500 py-8 text-center">Nenhum aluno atribuído a este curso.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Aluno</th>
                      <th className="py-2 pr-4">Progresso</th>
                      <th className="py-2 pr-4">Avaliação</th>
                      <th className="py-2 pr-4">Certificado</th>
                      <th className="py-2">Último acesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alunos.map((aluno) => (
                      <tr key={aluno.usuarioId} className="border-b border-gray-100">
                        <td className="py-3 pr-4">
                          <p className="font-medium text-gray-900">{aluno.nome}</p>
                          <p className="text-xs text-gray-500">{aluno.email}</p>
                        </td>
                        <td className="py-3 pr-4 min-w-[150px]">
                          <div className="flex items-center gap-2">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex-1">
                              <div
                                className={`h-full ${aluno.progresso >= 100 ? 'bg-green-600' : 'bg-primary-600'}`}
                                style={{ width: `${aluno.progresso}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-700 w-10 text-right">{aluno.progresso}%</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{aluno.aulasConcluidas}/{aluno.totalAulas} aulas</p>
                        </td>
                        <td className="py-3 pr-4">
                          {aluno.tentativas === 0 ? (
                            <span className="text-xs text-gray-500">Sem tentativa</span>
                          ) : (
                            <>
                              <p className={aluno.aprovado ? 'text-green-700 font-medium' : 'text-gray-700'}>
                                {aluno.melhorNota ?? 0}% · {aluno.aprovado ? 'Aprovado' : 'Não aprovado'}
                              </p>
                              <p className="text-xs text-gray-500">{aluno.tentativas} tentativa(s)</p>
                            </>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          {aluno.certificadoEmitido ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                              <Award size={14} /> Emitido
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">—</span>
                          )}
                          {aluno.certificadoCodigo && (
                            <p className="text-[10px] text-gray-400 font-mono mt-1">{aluno.certificadoCodigo}</p>
                          )}
                        </td>
                        <td className="py-3 text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={14} /> {formatarData(aluno.ultimoAcesso)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
